import React from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { AppModal } from '../../../components/AppModal';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import {
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
} from '../utils/settingsSectionStyles';

export type DkvEditSettings = {
  clientId: string;
  hasClientSecret: boolean;
  hasSubscriptionKey: boolean;
  customerIds: string[];
  syncEnabled: boolean;
};

export type DkvSaveResult = { testedOk: boolean; testError?: string | null };

function RequiredMark() {
  return <span className="text-red-500"> *</span>;
}

function SecretField({
  label,
  value,
  onChange,
  required,
  placeholder,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required: boolean;
  placeholder?: string;
  autoComplete?: string;
}) {
  const [visible, setVisible] = React.useState(false);
  return (
    <div>
      <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>
        {label}
        {required ? <RequiredMark /> : null}
      </label>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${SETTINGS_INPUT_CLASS} pr-10 font-mono`}
          autoComplete={autoComplete ?? 'new-password'}
          placeholder={placeholder}
          spellCheck={false}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-app-text-muted hover:bg-app-hover"
          aria-label={visible ? `${label} verbergen` : `${label} anzeigen`}
          title={visible ? 'Verbergen' : 'Anzeigen'}
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export function DkvConnectModal({
  open,
  editSettings,
  onClose,
  onSaved,
}: {
  open: boolean;
  editSettings: DkvEditSettings | null;
  onClose: () => void;
  onSaved: (result: DkvSaveResult) => void;
}) {
  const [clientId, setClientId] = React.useState('');
  const [clientSecret, setClientSecret] = React.useState('');
  const [subscriptionKey, setSubscriptionKey] = React.useState('');
  const [customerIds, setCustomerIds] = React.useState('');
  const [syncEnabled, setSyncEnabled] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const firstRef = React.useRef<HTMLInputElement>(null);

  const isEdit = Boolean(editSettings);
  const hasStoredSecret = Boolean(editSettings?.hasClientSecret);
  const hasStoredKey = Boolean(editSettings?.hasSubscriptionKey);

  React.useEffect(() => {
    if (!open) return;
    setClientId(editSettings?.clientId ?? '');
    setClientSecret('');
    setSubscriptionKey('');
    setCustomerIds((editSettings?.customerIds ?? []).join(', '));
    setSyncEnabled(editSettings?.syncEnabled ?? true);
    setSaving(false);
    setError(null);
    const t = window.setTimeout(() => firstRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open, editSettings]);

  useEscapeClose(onClose, open && !saving);

  const save = async () => {
    if (!clientId.trim()) {
      setError('Bitte die Client-ID eintragen.');
      return;
    }
    if (!clientSecret.trim() && !hasStoredSecret) {
      setError('Bitte das Client-Secret eintragen.');
      return;
    }
    if (!subscriptionKey.trim() && !hasStoredKey) {
      setError('Bitte den Subscription-Key eintragen.');
      return;
    }
    const ids = customerIds
      .split(/[,\s;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!ids.length) {
      setError('Bitte mindestens eine DKV-Kundennummer eintragen.');
      return;
    }
    if (ids.some((id) => !/^\d{1,10}$/.test(id))) {
      setError('Kundennummern bestehen aus bis zu 10 Ziffern.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/settings/dkv', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, clientSecret, subscriptionKey, customerIds: ids, syncEnabled }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Speichern fehlgeschlagen.');
      onSaved({ testedOk: Boolean(d?.testedOk), testError: d?.testError ?? null });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      closeOnOverlayClick={false}
      disableClose={saving}
      overlayClassName="z-50"
      panelClassName="md:max-w-lg md:h-auto"
      ariaLabelledBy="dkv-connect-title"
    >
      <div className="flex flex-col flex-1 min-h-0 h-full">
        <div className="px-5 py-3 border-b border-app-border flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2 id="dkv-connect-title" className="text-sm font-semibold text-app-text">
              {isEdit ? 'DKV-Zugang bearbeiten' : 'DKV Mobility verbinden'}
            </h2>
            <p className="text-xs text-app-text-subtle mt-0.5">
              {isEdit
                ? 'Zugangsdaten ändern und erneut prüfen. Leere Geheimnis-Felder bleiben unverändert.'
                : 'Zugangsdaten aus der DKV-Onboarding-Mail eintragen. Das Secret kommt per SMS.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-app-border text-app-text-subtle hover:bg-app-hover hover:text-app-text disabled:opacity-40"
            aria-label="Schließen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          {error ? (
            <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
          ) : null}
          <div>
            <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>
              Client-ID
              <RequiredMark />
            </label>
            <input
              ref={firstRef}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className={`${SETTINGS_INPUT_CLASS} font-mono`}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <SecretField
            label="Client-Secret"
            value={clientSecret}
            onChange={setClientSecret}
            required={!hasStoredSecret}
            placeholder={hasStoredSecret ? 'Unverändert lassen' : undefined}
          />
          <SecretField
            label="Subscription-Key"
            value={subscriptionKey}
            onChange={setSubscriptionKey}
            required={!hasStoredKey}
            placeholder={hasStoredKey ? 'Unverändert lassen' : undefined}
            autoComplete="off"
          />
          <div>
            <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>
              DKV-Kundennummern
              <RequiredMark />
            </label>
            <input
              value={customerIds}
              onChange={(e) => setCustomerIds(e.target.value)}
              className={`${SETTINGS_INPUT_CLASS} font-mono`}
              placeholder="z. B. 0000733331, 0000733332"
              inputMode="numeric"
              autoComplete="off"
            />
            <p className="mt-1 text-xs text-app-text-subtle">
              10-stellig, mehrere mit Komma trennen (maximal 10). Nur Nummern, die DKV für den technischen Benutzer freigegeben hat.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-app-text">
            <input type="checkbox" checked={syncEnabled} onChange={(e) => setSyncEnabled(e.target.checked)} />
            Tankungen täglich automatisch abrufen
          </label>
          <div className="flex justify-end pt-1">
            <button type="button" className={SETTINGS_BTN_PRIMARY} disabled={saving} onClick={() => void save()}>
              {saving ? <LoadingSpinner size="sm" /> : null}
              Speichern und prüfen
            </button>
          </div>
        </div>
      </div>
    </AppModal>
  );
}
